/**
 * Utility functions for formatting data
 */

/**
 * Format amount to rounded figure (no decimal places)
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency symbol (default: '₹')
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount, currency = '₹') => {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return `${currency}0`;
    }
    
    // Round to nearest integer
    const roundedAmount = Math.round(parseFloat(amount));
    return `${currency}${roundedAmount.toLocaleString()}`;
};

/**
 * Format amount with decimal places (for detailed views)
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency symbol (default: '₹')
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted amount
 */
export const formatAmountDetailed = (amount, currency = '₹', decimals = 2) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return `${currency}0.00`;
    }
    
    return `${currency}${parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })}`;
};

/**
 * Format percentage
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '0%';
    }
    
    return `${parseFloat(value).toFixed(decimals)}%`;
};

/**
 * Format date
 * @param {string|Date} date - The date to format
 * @param {string} format - Date format (default: 'short')
 * @returns {string} Formatted date
 */
/**
 * Format a Date as YYYY-MM-DD in the browser's local timezone.
 * Use this instead of toISOString().split('T')[0], which converts to UTC first
 * and so returns the previous day for local midnight anywhere east of UTC (IST).
 * @param {Date} date
 * @returns {string}
 */
export const toLocalYmd = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export const formatDate = (date, format = 'short') => {
    if (!date) return '';
    
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    switch (format) {
        case 'long':
            return dateObj.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        case 'medium':
            return dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        case 'short':
        default:
            return dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
    }
};
